if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.initViewerNavigation();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.thumbnailSlider = this.querySelector('.thumbnail-slider');
        if (this.thumbnailSlider && this.elements.viewer) {
          this.syncVerticalThumbnailHeight = this.syncVerticalThumbnailHeight.bind(this);
          this.onThumbnailMediaQueryChange = this.onThumbnailMediaQueryChange.bind(this);
          requestAnimationFrame(() => {
            this.initVerticalThumbnailHeight();
          });
        }
      }

      connectedCallback() {
        if (this.viewerResizeObserver || !this.thumbnailSlider || !this.elements.viewer) return;
        this.initVerticalThumbnailHeight();
      }

      initVerticalThumbnailHeight() {
        if (this.viewerResizeObserver) return;

        this.viewerResizeObserver = new ResizeObserver(() => this.syncVerticalThumbnailHeight());
        this.viewerResizeObserver.observe(this.elements.viewer);
        this.mql.addEventListener('change', this.onThumbnailMediaQueryChange);

        this.elements.viewer.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', this.syncVerticalThumbnailHeight, { once: true });
        });

        this.syncVerticalThumbnailHeight();
      }

      disconnectedCallback() {
        this.viewerResizeObserver?.disconnect();
        this.mql?.removeEventListener('change', this.onThumbnailMediaQueryChange);
        this.mql?.removeEventListener('change', this.onViewerNavigationMediaQueryChange);
      }

      onThumbnailMediaQueryChange() {
        this.syncVerticalThumbnailHeight();
      }

      syncVerticalThumbnailHeight() {
        if (!this.thumbnailSlider || !this.elements.viewer) return;

        const useVerticalThumbnails =
          this.mql.matches && this.dataset.desktopLayout.includes('thumbnail');

        if (!useVerticalThumbnails) {
          this.thumbnailSlider.style.maxHeight = '';
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('.product__media-item');
        const viewerHeight = activeMedia ? activeMedia.offsetHeight : this.elements.viewer.offsetHeight;

        this.thumbnailSlider.style.maxHeight = `${viewerHeight}px`;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;

        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        this.updateViewerButtons();
        this.syncVerticalThumbnailHeight();
      }

      initViewerNavigation() {
        const prevButton = this.elements.viewer?.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer?.querySelector('button[name="next"]');
        if (!prevButton || !nextButton) return;

        prevButton.addEventListener(
          'click',
          (event) => {
            if (!this.shouldUseViewerNavigation()) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.switchMediaByStep(-1);
          },
          true
        );

        nextButton.addEventListener(
          'click',
          (event) => {
            if (!this.shouldUseViewerNavigation()) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.switchMediaByStep(1);
          },
          true
        );

        this.onViewerNavigationMediaQueryChange = this.updateViewerButtons.bind(this);
        this.mql.addEventListener('change', this.onViewerNavigationMediaQueryChange);
        this.updateViewerButtons();
      }

      shouldUseViewerNavigation() {
        return (
          this.mql.matches &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          !!this.elements.thumbnails
        );
      }

      switchMediaByStep(step) {
        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const activeIndex = thumbnails.findIndex((thumbnail) =>
          thumbnail.querySelector('button[aria-current="true"]')
        );
        const nextIndex = activeIndex + step;
        if (nextIndex < 0 || nextIndex >= thumbnails.length) return;
        this.setActiveMedia(thumbnails[nextIndex].dataset.target, false);
      }

      updateViewerButtons() {
        const prevButton = this.elements.viewer?.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer?.querySelector('button[name="next"]');
        if (!prevButton || !nextButton || !this.elements.thumbnails) return;

        if (!this.shouldUseViewerNavigation()) {
          prevButton.removeAttribute('disabled');
          nextButton.removeAttribute('disabled');
          return;
        }

        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const activeIndex = thumbnails.findIndex((thumbnail) =>
          thumbnail.querySelector('button[aria-current="true"]')
        );

        if (activeIndex <= 0) prevButton.setAttribute('disabled', 'disabled');
        else prevButton.removeAttribute('disabled');

        if (activeIndex >= thumbnails.length - 1) nextButton.setAttribute('disabled', 'disabled');
        else nextButton.removeAttribute('disabled');
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const slider = this.elements.thumbnails.slider;
        const useVerticalThumbnails =
          this.mql.matches && this.dataset.desktopLayout.includes('thumbnail');

        if (useVerticalThumbnails && slider) {
          const top = thumbnail.offsetTop;
          const bottom = top + thumbnail.offsetHeight;
          const visible = top >= slider.scrollTop && bottom <= slider.scrollTop + slider.clientHeight;
          if (visible) return;

          slider.scrollTo({ top, behavior: 'smooth' });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
